// @flow
import * as React from 'react'

import { listAuditLogs, restartAuditLog } from './api'
import type {
  AuditLogContextProps,
  AuditLogFilterProps,
  AuditLogProps,
  AuditLogQueryProps,
  AuditLogResponseProps,
} from './types'

export const AuditLogContext: React.Context<AuditLogContextProps> =
  React.createContext<AuditLogContextProps>({
    auditLogs: [],
    auditLogsLoading: true,
    auditLogsCount: 0,
    auditLogsPages: 0,
    currentPage: 0,
    selectedAuditLogs: [],
    auditLogsQuery: {},
    toggleSelectedAuditLog: (auditLog: AuditLogProps) => {},
    makeSearch: (search: string | void) => {},
    makeFilter: (filter: AuditLogFilterProps) => {},
    makeSort: (field: string) => {},
    setPage: (page: number, pageSize?: number) => {},
    restartSelectedsAuditLog: () => {},
  })

type Props = {
  children: React.Node,
}

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_QUERY: AuditLogQueryProps = {
  ordering: undefined,
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  search: undefined,
  form: undefined,
  document: undefined,
  is_archived: undefined,
  is_submitted: undefined,
  packet: undefined,
  sent_at__gte: undefined,
  sent_at__lte: undefined,
  submitted_at__gte: undefined,
  submitted_at__lte: undefined,
  user: undefined,
}

export function AuditLogProvider({ children }: Props): React.Node {
  const [auditLogs, setAuditLogs] = React.useState<AuditLogProps[]>([])
  const [auditLogsLoading, setAuditLogsLoading] = React.useState<boolean>(true)
  const [auditLogsCount, setAuditLogsCount] = React.useState<number>(0)
  const [auditLogsPages, setAuditLogsPages] = React.useState<number>(0)
  const [currentPage, setCurrentPage] = React.useState<number>(0)
  const [auditLogsQuery, setAuditLogsQuery] =
    React.useState<AuditLogQueryProps>(DEFAULT_QUERY)
  const [selectedAuditLogs, setSelectedAuditLogs] = React.useState<
    AuditLogProps[],
  >([])

  const processResponse = React.useCallback(
    (response: AuditLogResponseProps, _auditLogsQuery: AuditLogQueryProps) => {
      setAuditLogs(response.results)
      setAuditLogsCount(response.count)
      setAuditLogsPages(
        Math.ceil(
          (response.count ?? 1) /
            (_auditLogsQuery.page_size ?? DEFAULT_PAGE_SIZE)
        )
      )
      setCurrentPage(_auditLogsQuery.page ?? 1)
    },
    []
  )

  React.useEffect(() => {
    setAuditLogsLoading(true)
    listAuditLogs(auditLogsQuery)
      .then((response) => {
        processResponse(response, auditLogsQuery)
        setAuditLogsLoading(false)
      })
      .catch((err) => {
        setAuditLogsLoading(false)
      })
  }, [processResponse, auditLogsQuery])

  const makeSearch = React.useCallback((search: string | void) => {
    setAuditLogsQuery((prevQuery: AuditLogQueryProps): AuditLogQueryProps => ({
      ...prevQuery,
      search,
      page: 1,
    }))
  }, [])

  const makeFilter = React.useCallback((filter: AuditLogFilterProps) => {
    setAuditLogsQuery(
      (prevQuery: $Exact<AuditLogQueryProps>): AuditLogQueryProps => ({
        ...prevQuery,
        ...filter,
        page: 1,
      })
    )
  }, [])

  const makeSort = React.useCallback((field: string) => {
    setAuditLogsQuery((prevQuery: AuditLogQueryProps): AuditLogQueryProps => ({
      ...prevQuery,
      ordering: field,
      page: 1,
    }))
  }, [])

  const setPage = React.useCallback(
    (page: number, pageSize?: number) => {
      setAuditLogsQuery(
        (prevQuery: AuditLogQueryProps): AuditLogQueryProps => ({
          ...prevQuery,
          page,
          page_size: pageSize || prevQuery.page_size,
        })
      )
    },
    [setAuditLogsQuery]
  )

  const toggleSelectedAuditLog = React.useCallback(
    (auditLog: AuditLogProps) => {
      setSelectedAuditLogs(
        (prevSelectedAuditLogs: AuditLogProps[]): AuditLogProps[] => {
          const index = prevSelectedAuditLogs.findIndex(
            (r) => r?.uid === auditLog?.uid
          )
          if (index === -1) {
            return [...prevSelectedAuditLogs, auditLog]
          }
          return [
            ...prevSelectedAuditLogs.slice(0, index),
            ...prevSelectedAuditLogs.slice(index + 1),
          ]
        }
      )
    },
    []
  )

  const restartSelectedsAuditLog = React.useCallback(() => {
    const uids = selectedAuditLogs.map((auditLog) => auditLog?.uid)
    setAuditLogsLoading(true)
    restartAuditLog(uids)
      .then(() => {
        setSelectedAuditLogs([])
        setAuditLogsQuery(DEFAULT_QUERY)
        setAuditLogsLoading(false)
      })
      .catch(() => {
        setAuditLogsLoading(false)
      })
  }, [selectedAuditLogs])

  const props = React.useMemo(
    () => ({
      auditLogs,
      auditLogsLoading,
      auditLogsCount,
      auditLogsPages,
      currentPage,
      selectedAuditLogs,
      auditLogsQuery,
      toggleSelectedAuditLog,
      makeSearch,
      makeFilter,
      makeSort,
      setPage,
      restartSelectedsAuditLog,
    }),
    [
      auditLogs,
      auditLogsLoading,
      auditLogsCount,
      auditLogsPages,
      selectedAuditLogs,
      auditLogsQuery,
      toggleSelectedAuditLog,
      currentPage,
      makeSearch,
      makeFilter,
      makeSort,
      setPage,
      restartSelectedsAuditLog,
    ]
  )

  return (
    <AuditLogContext.Provider value={props}>
      {children}
    </AuditLogContext.Provider>
  )
}

export default AuditLogProvider
